import { DroppableZone } from "@/lib/dnd/components/DroppableZone";
import { useQuestionHandlers } from "@/lib/dnd/hooks/useQuestionHandlers";
import { useLocalImageUpload } from "@/lib/hooks/slides/editor/useLocalImageUpload";
import { useAppDispatch } from "@/store/hooks";
import { updateElementProps } from "@/store/slideEditorSlice";
import { imageChoiceStyles } from "@ai-teach/canvas-styles";
import type { ImageChoiceElement } from "@ai-teach/types";
import { AiFillPicture } from "react-icons/ai";
import { DeleteImageButton } from "./DeleteImageButton";
import { QuestionCheckbox } from "./QuestionCheckbox";
import { gradedRingStyle } from "./gradedStyles";

interface ImageChoiceRendererProps {
  element: ImageChoiceElement;
  isLiveMode?: boolean;
  /** The student picked this choice in the play being replayed. */
  isPicked?: boolean | undefined;
  isChoiceFrozen?: boolean | undefined;
  isFrozen?: boolean | undefined;
}

export function ImageChoiceRenderer({
  element,
  isLiveMode = false,
  isPicked,
  isChoiceFrozen,
  isFrozen,
}: ImageChoiceRendererProps) {
  const dispatch = useAppDispatch();

  const { handleQuestionDragAndDrop } = useQuestionHandlers();
  const { handleFileDrop } = useLocalImageUpload((media, containerId) =>
    handleQuestionDragAndDrop(media, containerId)
  );

  const handleToggleIsCorrectAnswer = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    dispatch(
      updateElementProps({
        id: element.id ?? "",
        props: {
          isCorrectAnswer: !element.isCorrectAnswer,
        },
      })
    );
  };

  const handleDeleteImage = (event: React.MouseEvent) => {
    event.stopPropagation();

    dispatch(
      updateElementProps({
        id: element.id ?? "",
        props: {
          media: {
            id: null,
            src: null,
            filename: null,
          },
        },
      })
    );
  };

  // See TextChoiceRenderer: an unpicked correct answer waits for the whole type group to lock.
  const graded = gradedRingStyle(
    element.isCorrectAnswer
      ? isFrozen || (isPicked && isChoiceFrozen)
        ? "correct"
        : undefined
      : isPicked && isChoiceFrozen
        ? "wrong"
        : undefined
  );

  return (
    <div
      style={{
        ...imageChoiceStyles.outerDiv,
        borderRadius: element.border.cornerRadius,
        pointerEvents: isLiveMode ? "none" : "auto",
        ...graded,
      }}
    >
      {/* Checkbox  */}
      {!isLiveMode && (
        <QuestionCheckbox
          isCorrectAnswer={element.isCorrectAnswer}
          handleToggleIsCorrectAnswer={(e) => handleToggleIsCorrectAnswer(e)}
          position="top-left"
        />
      )}

      {isLiveMode ? (
        <div style={imageChoiceStyles.imageContainer}>
          {/* Image content */}
          {element.media?.src ? (
            <img
              src={element.media.src}
              alt={element.media?.filename ?? "image"}
              draggable={false}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <AiFillPicture fontSize={40} />
          )}
        </div>
      ) : (
        <DroppableZone
          id={`imageChoice-${element.id}`}
          data={{ id: `question//imageChoice//${element.id}//0`, type: "imageChoice" }}
          allowedType={["image"]}
          style={{
            ...imageChoiceStyles.imageContainer,
            borderRadius: element.border.cornerRadius,
          }}
          defaultBorderClass={element.media.src ? "" : "border-2 border-dashed border-slate-300"}
          onLocalFileDrop={(file) =>
            handleFileDrop(file, `question//imageChoice//${element.id}//0`)
          }
        >
          {/* Image content */}
          {element.media?.src ? (
            <div
              style={{ position: "relative", width: "100%", height: "100%" }}
              className="group/image"
            >
              <img
                src={element.media.src}
                alt={element.media?.filename ?? "image"}
                draggable={false}
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              <DeleteImageButton onClick={handleDeleteImage} />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center">
              <AiFillPicture className="object-contain rounded" fontSize={40} />
              <p className="text-sm">Drop image here</p>
            </div>
          )}
        </DroppableZone>
      )}
    </div>
  );
}
